import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { newIdempotencyKey } from '../client/idempotency';
/**
 * The four ways a member reacts to a content item.
 *
 * Identical to community's `ReactionKind` and spelled separately on purpose:
 * the backend pins the three spellings to the same members in a unit test, and
 * a shared alias here would hide the day one of them diverges.
 */
export const ContentReactionKind = z.enum(['HEART', 'ANCHOR', 'HUG', 'LIGHTBULB']);
/**
 * What members did TO a content item — kept, shared, asked-too, and reacted.
 *
 * `POST /content/{id}/save` is literally documented as « Kept in heart », the
 * design's own words for the heart on the anchoring phrase. A phrase is a
 * content item like any other, so the same pair drives the phrase strip and
 * anything else that grows a keep control later.
 *
 * ── `myReactions` is per CALLER, and it is the only such field here ───────
 * There is still no "did I keep this" or "did I share this" flag, so a client
 * cannot restore those pressed states after a reload and should hold its own
 * optimistic state for them. Reactions are different: `myReactions` comes back
 * on every read that asks for signals AND on both writes, so a card can redraw
 * its own filled glyph from the response rather than re-reading the list.
 *
 * All four counts are always present, zero when nobody reacted. The enclosing
 * `signals` field is what carries *not asked for*, by being null — an empty
 * reactions object would spend that meaning a second time.
 */
export const ContentSignals = z.object({
    saves: z.number().int().nonnegative(),
    shares: z.number().int().nonnegative(),
    askedToo: z.number().int().nonnegative(),
    reactions: z.object({
        HEART: z.number().int().nonnegative(),
        ANCHOR: z.number().int().nonnegative(),
        HUG: z.number().int().nonnegative(),
        LIGHTBULB: z.number().int().nonnegative(),
    }),
    myReactions: z.array(ContentReactionKind),
});
/**
 * What members wrote ABOUT it — a sibling of `signals`, not part of it.
 *
 * A thread is not a gesture. `signals` counts what was done to the item;
 * this counts the discussion under it, which is why the design's `💬 2` reads
 * from here and its `❤️ 26` does not.
 *
 * Posts and comments stay separate. A total we performed could not be taken
 * apart again, and the design draws the two counts differently anyway.
 */
export const ContentDiscussion = z.object({
    posts: z.number().int().nonnegative(),
    comments: z.number().int().nonnegative(),
});
export function useKeepContent(http) {
    return useMutation({
        /*
         * `childId` is optional and attributes the keep to one child, the same way
         * the progress writes do — it is what makes `CrewChild.counts.anchorsSaved`
         * possible. Omitted, the line is simply the parent's.
         */
        mutationFn: ({ id, keep, childId }) => http.post(`/content/${id}/${keep ? 'save' : 'save/remove'}`, childId ? { childId } : undefined, {
            schema: ContentSignals,
            idempotencyKey: newIdempotencyKey(),
        }),
    });
}
export function useShareContent(http) {
    return useMutation({
        mutationFn: (id) => http.post(`/content/${id}/share`, undefined, {
            schema: ContentSignals,
            idempotencyKey: newIdempotencyKey(),
        }),
    });
}
/**
 * React to a content item, and take it back.
 *
 * ── No idempotency key, deliberately ─────────────────────────────────────
 * Every other write in this client carries one because a repeat would create a
 * second row. This one cannot: `content_signals` is keyed
 * `(item, member, kind)`, so reacting twice with the same kind writes nothing
 * and the second call is already the same operation as the first. A key here
 * would be ceremony around a table that is doing the work.
 *
 * Both return the whole `ContentSignals`, including `myReactions` for this
 * caller — so the pressed state comes from the response and never from a guess.
 *
 * `REACTION_KIND_DISABLED` is refused on the way IN only. An administrator
 * turning a kind off stops it being added and leaves what exists counted, so a
 * member who reacted before the toggle moved can still undo it — which is why
 * removal has no such check and this pair is two hooks rather than a toggle.
 */
export function useReactToContent(http) {
    return useMutation({
        mutationFn: ({ id, kind }) => http.post(`/content/${id}/react`, { kind }, { schema: ContentSignals }),
    });
}
export function useRemoveContentReaction(http) {
    return useMutation({
        mutationFn: ({ id, kind }) => http.post(`/content/${id}/react/remove`, { kind }, { schema: ContentSignals }),
    });
}
//# sourceMappingURL=signals.js.map